import React from 'react';
import { tintFromAccent } from '../steps/_shared';

interface ProgressBarProps {
  /** Station brand / accent color used for the fill. */
  accentColor?: string;
  /** Caption above the track. When `percent` is set, appends ` N%`. */
  label?: string;
  className?: string;
  /** Track thickness. Default `md`. */
  size?: 'sm' | 'md';
  /**
   * Real 0–100 progress (e.g. XHR upload bytes). Omit for a true
   * indeterminate wait (validate / unknown duration) — a sliding segment,
   * not a fake climb toward 90%.
   */
  percent?: number;
  /** When false, determinate mode snaps to 100%. Ignored without `percent`. */
  active?: boolean;
}

/**
 * Full-width progress track for creative upload / validate waits.
 */
const ProgressBar: React.FC<ProgressBarProps> = ({
  accentColor = '#3b82f6',
  label,
  className = '',
  size = 'md',
  percent: percentProp,
  active = true,
}) => {
  const trackH = size === 'sm' ? 'h-2' : 'h-2.5';
  const determinate = typeof percentProp === 'number';
  const percent = determinate
    ? Math.max(0, Math.min(100, active ? percentProp : 100))
    : null;
  const rounded = percent === null ? null : Math.round(percent);
  const caption =
    label && rounded !== null ? `${label} ${rounded}%` : label ?? null;

  return (
    <div
      className={`w-full ${className}`}
      role="progressbar"
      aria-busy={active}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={rounded ?? undefined}
      aria-valuetext={caption ?? (rounded !== null ? `${rounded}%` : label ?? 'Loading')}
      aria-label={label ?? 'Loading'}
    >
      {caption ? (
        <p className="text-[12px] text-slate-500 mb-2 leading-snug">{caption}</p>
      ) : null}
      <div
        className={`relative ${trackH} w-full overflow-hidden rounded-full`}
        style={{ backgroundColor: tintFromAccent(accentColor, 0.18) }}
      >
        {determinate ? (
          <div
            className={`${trackH} rounded-full`}
            style={{
              width: `${percent}%`,
              backgroundColor: accentColor,
              transition: 'width 100ms linear',
            }}
          />
        ) : (
          <>
            <div
              className={`absolute inset-y-0 w-1/3 rounded-full`}
              style={{
                backgroundColor: accentColor,
                animation: 'ss-progress-indeterminate 1.15s ease-in-out infinite',
              }}
            />
            <style>{`
              @keyframes ss-progress-indeterminate {
                0% { left: -33%; }
                100% { left: 100%; }
              }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
